import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  Menu,
  Moon,
  Search,
  Settings,
  Sun,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { NotificationBell } from "@/components/NotificationBell";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuthStore } from "@/store/authStore";
import { useUiStore } from "@/store/uiStore";
import { cn } from "@/utils/cn";

export function Header() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const toggleMobileSidebar = useUiStore((s) => s.toggleMobileSidebar);
  const darkMode = useUiStore((s) => s.darkMode);
  const setDarkMode = useUiStore((s) => s.setDarkMode);
  const t = useUiStore((s) => s.t);
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    const next = q.trim();
    if (next.length < 2) return;
    navigate(`/search?q=${encodeURIComponent(next)}`);
  }

  // Dismiss user menu on click outside or Escape
  useEffect(() => {
    if (!userMenuOpen) return;

    function handleDocClick(e: MouseEvent) {
      if (!userMenuRef.current?.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [userMenuOpen]);

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-14 w-full shrink-0 items-center justify-between gap-4 border-b border-slate-200/80 bg-white/95 px-3.5 backdrop-blur-xs transition-colors md:px-5 dark:border-zinc-800/80 dark:bg-zinc-950/95">
      {/* Left section: mobile trigger / logo & desktop search positioned to the left */}
      <div className="flex flex-1 items-center gap-2.5 md:gap-3 max-w-md">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleMobileSidebar}
          aria-label={t("app.toggleSidebar")}
          className="md:hidden"
          data-testid="mobile-sidebar-toggle"
        >
          <Menu className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2 md:hidden">
          <BrandLogo className="h-7 w-7 rounded-lg" />
          <span className="text-sm font-bold tracking-tight text-slate-900 dark:text-zinc-50">
            MarketIQ
          </span>
        </div>

        {/* Global search positioned to the left */}
        <form onSubmit={onSearch} className="relative hidden w-full md:block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("app.searchPlaceholder")}
            aria-label={t("app.searchPlaceholder")}
            className="h-9 w-full rounded-lg border border-slate-200/90 bg-slate-50/70 pl-9 pr-10 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-[var(--brand-primary)] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)] dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-[var(--brand-primary)] dark:focus:bg-zinc-900"
          />
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 select-none items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400 shadow-2xs lg:inline-flex dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
            /
          </kbd>
        </form>
      </div>

      {/* Right section: Actions & user profile */}
      <div className="flex items-center gap-1.5 md:gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden"
          onClick={() => navigate("/search")}
          aria-label={t("pages.search.title")}
        >
          <Search className="h-4 w-4" />
        </Button>

        <NotificationBell />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDarkMode(!darkMode)}
          aria-label={t("app.toggleDark")}
        >
          {darkMode ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-slate-600 dark:text-zinc-400" />}
        </Button>

        {/* User profile dropdown */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((v) => !v)}
            aria-expanded={userMenuOpen}
            aria-haspopup="true"
            aria-label="User account menu"
            className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-slate-100 dark:hover:bg-zinc-800/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
            data-testid="header-user-menu-trigger"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--brand-primary)] font-semibold text-xs text-white shadow-2xs dark:bg-[var(--brand-primary)]">
              {initials}
            </div>
            <div className="hidden text-left sm:block">
              <div className="max-w-[120px] truncate text-xs font-semibold text-slate-900 dark:text-zinc-50">
                {user?.name || "User"}
              </div>
              <div className="max-w-[120px] truncate text-[10px] text-slate-500 dark:text-zinc-400">
                {user?.email || ""}
              </div>
            </div>
            <ChevronDown
              className={cn(
                "hidden h-3.5 w-3.5 text-slate-400 transition-transform sm:block dark:text-zinc-500",
                userMenuOpen && "rotate-180",
              )}
            />
          </button>

          {userMenuOpen ? (
            <div
              role="menu"
              aria-label="User account options"
              className="absolute right-0 top-full mt-1.5 w-56 rounded-xl border border-slate-200/90 bg-white p-1.5 shadow-xl transition-all dark:border-zinc-800 dark:bg-zinc-950 z-50 animate-in fade-in-0 zoom-in-95 duration-100"
              data-testid="header-user-dropdown"
            >
              <div className="border-b border-slate-100 px-3 py-2.5 dark:border-zinc-800/80">
                <div className="truncate text-xs font-semibold text-slate-900 dark:text-zinc-50">
                  {user?.name || "User"}
                </div>
                <div className="truncate text-[11px] text-slate-500 dark:text-zinc-400">
                  {user?.email}
                </div>
                {user?.role ? (
                  <div className="mt-1.5 inline-flex items-center rounded-full bg-[var(--brand-soft)] px-2 py-0.5 text-[10px] font-medium text-[var(--brand-primary)]">
                    {user.role}
                  </div>
                ) : null}
              </div>

              <div className="py-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/profile");
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-300 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
                >
                  <User className="h-3.5 w-3.5 text-slate-400" />
                  <span>Profile</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/settings");
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-300 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
                >
                  <Settings className="h-3.5 w-3.5 text-slate-400" />
                  <span>Settings</span>
                </button>
              </div>

              <div className="border-t border-slate-100 pt-1 dark:border-zinc-800/80">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                  data-testid="dropdown-signout"
                >
                  <LogOut className="h-3.5 w-3.5 text-red-500" />
                  <span>{t("app.signOut")}</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Desktop direct Sign Out action */}
        <Button
          variant="outline"
          size="sm"
          onClick={logout}
          className="hidden xl:inline-flex"
          data-testid="header-signout"
        >
          {t("app.signOut")}
        </Button>
      </div>
    </header>
  );
}
